/**
 * The frame: navigation, session gate, sign-out.
 *
 * THE PATIENT COMES FROM THE SESSION, NEVER FROM THE URL.
 *
 * The clinician app routes on `/patients/$patientId` because a clinician
 * legitimately moves between patients. This portal has exactly one, and it is
 * whoever the token says it is — `session.profileRef`. There is no route
 * parameter to change, no id in a link, and nothing to tamper with.
 *
 * That is defence in depth rather than the defence itself: §1.6 puts access
 * control at the data layer, and the gateway refuses another patient's record
 * regardless of what this app asks for (proven live by section 10 of the Phase
 * 1 criterion). But a URL that cannot express the wrong request is a URL
 * nobody has to be refused for.
 *
 * The session gate below is likewise a convenience, not a control. Hiding a
 * link hides nothing from anyone who can type.
 */

import { Link, Outlet } from '@tanstack/react-router';
import { useState, type JSX } from 'react';

import { LoginScreen } from './components/LoginScreen.js';
import { readSession, signOut, type Session } from './lib/auth.js';

export const AppShell = (): JSX.Element => {
  const [session, setSession] = useState<Session | null>(() => readSession());

  if (session === null) {
    return <LoginScreen onSignedIn={setSession} />;
  }

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <header className="shell__header">
        <nav aria-label="Primary">
          <ul>
            <li>
              <Link to="/">My health</Link>
            </li>
            <li>
              <Link to="/results">My results</Link>
            </li>
            <li>
              <Link to="/access">Who has seen my record</Link>
            </li>
            <li>
              <Link to="/permissions">Who can see what</Link>
            </li>
          </ul>
        </nav>

        <div className="shell__session">
          <span>Signed in as {session.email}</span>
          <button
            type="button"
            onClick={() => {
              signOut();
              setSession(null);
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      <main id="main">
        <Outlet />
      </main>
    </div>
  );
};
